'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { api, SiteConfig } from '@/lib/api';
import { useAuth } from '@/providers/AuthProvider';

const DEFAULTS = {
    footer_address: 'Via Panorâmica S/N, 4150-564 Porto, Portugal',
    footer_phone: '+351 22 605 71 00',
    footer_email: 'ceau@arquitectura.up.pt',
};

function EditableContact({ value, field, isRoot, onSave }: {
    value: string;
    field: 'footer_address' | 'footer_phone' | 'footer_email';
    isRoot: boolean;
    onSave: (field: string, value: string) => void;
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);

    useEffect(() => { setDraft(value); }, [value]);

    const save = async () => {
        setEditing(false);
        const trimmed = draft.trim();
        if (trimmed === value) return;
        await api.updateSiteConfig({ [field]: trimmed });
        onSave(field, trimmed);
    };

    if (!isRoot) return <p>{value}</p>;

    if (editing) {
        return (
            <input
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={save}
                onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setDraft(value); setEditing(false); } }}
                className="bg-transparent border-b border-slate-500 outline-none text-gray-400 w-full"
            />
        );
    }

    return (
        <p
            onClick={() => setEditing(true)}
            title="Click to edit"
            className="cursor-pointer hover:text-white transition-colors"
        >
            {value}
        </p>
    );
}

export function Footer() {
    const t = useTranslations('footer');
    const { user } = useAuth();
    const isRoot = user?.role === 'root';

    const [contacts, setContacts] = useState({
        footer_address: DEFAULTS.footer_address,
        footer_phone: DEFAULTS.footer_phone,
        footer_email: DEFAULTS.footer_email,
    });

    useEffect(() => {
        api.getSiteConfig().then((cfg: SiteConfig) => {
            setContacts({
                footer_address: cfg.footer_address || DEFAULTS.footer_address,
                footer_phone: cfg.footer_phone || DEFAULTS.footer_phone,
                footer_email: cfg.footer_email || DEFAULTS.footer_email,
            });
        }).catch(() => {});
    }, []);

    return (
        <footer className="w-full bg-[#333333] text-white py-12 mt-auto print:hidden">
            <div className="max-w-8xl mx-auto px-10 grid grid-cols-1 md:grid-cols-2 gap-8 text-sm">
                <div>
                    <h3 className="font-medium mb-1 text-[#888888]">MODUS</h3>
                    <p className="text-gray-400 text-sm">MODUlar architecture rating System</p>
                </div>

                <div>
                    <h3 className="font-medium mb-4 text-[#888888]">{t('contacts')}</h3>
                    <div className="space-y-2 text-gray-400">
                        <EditableContact value={contacts.footer_address} field="footer_address" isRoot={isRoot} onSave={(f, v) => setContacts(c => ({ ...c, [f]: v }))} />
                        <EditableContact value={contacts.footer_phone} field="footer_phone" isRoot={isRoot} onSave={(f, v) => setContacts(c => ({ ...c, [f]: v }))} />
                        <EditableContact value={contacts.footer_email} field="footer_email" isRoot={isRoot} onSave={(f, v) => setContacts(c => ({ ...c, [f]: v }))} />
                    </div>
                </div>
            </div>
        </footer>
    );
}
